import { Dispatch } from 'redux';
import { all, call, fork, put, take, takeLatest } from 'redux-saga/effects';
import { registerResumeContextHandler, unregisterResumeContextHandler } from '../audioContext';
import { UnityEngineAppConfig } from '../config';
import { IBuildConfiguration, IUnityInstance, ProgressCallback, getConfig } from '../loader';
import * as ActionTypes from './actionTypes';
import * as Actions from './actions';
import { registerFullscreenHandler, toggleFullscreenOff, unregisterFullscreenHandler } from './fullscreen';

let instance: IUnityInstance;

declare global {
  interface Window {
    createUnityInstance: (
      canvas: HTMLCanvasElement,
      config: IBuildConfiguration,
      onProgress: ProgressCallback
    ) => Promise<IUnityInstance>;
    appReady: () => void;
    engineReady: () => void;
    exerciseReady: () => void;
    completeExercise: (result: string) => void;
    exerciseFailed: (e: Error) => void;
    onError: (e: Error) => void;
    onAlert: (message: string) => void;
    gameplaySoundSettingsUpdated: (result: number) => void;
    gameplayBackgroundSettingsUpdated: (result: number) => void;
    gameplayFullscreenSettingsUpdated: (result: number) => void;
    saveStats: (statId: string, payload: string) => void;
    trackEvent: (eventData: string) => void;
  }
}

let startTime: number;
let endTime: number;
const timerName = 'timer';
function getTimestamp() {
  return Math.trunc(Date.now() / 1000);
}

let loaderScript: HTMLScriptElement;
export function* unityInitSaga(dispatch: Dispatch, action: ActionTypes.IUnityInitAction) {
  if (instance) {
    // if unity is instantiated - quit current instance
    yield call(instance.Quit);
    yield call(unregisterResumeContextHandler);
  }
  if (loaderScript) {
    // if loader script already added - remove it from dom
    document.body.removeChild(loaderScript);
  }
  // unregister any previous fullscreen handlers
  yield call(unregisterFullscreenHandler);
  const { basePath } = action.payload;
  startTime = getTimestamp();
  console.time(timerName);
  yield put(Actions.unityLog(`Unity engine start at ${startTime}`));
  // app started handler
  window.appReady = () => dispatch(Actions.unityAppReady());
  // 'engine ready' handler
  window.engineReady = () => dispatch(Actions.unityEngineReady());
  // 'assets loaded' handler
  window.exerciseReady = () => dispatch(Actions.unityExerciseReady());
  // exercise completed callback
  window.completeExercise = (result: string) => {
    dispatch(Actions.unityExerciseComplete(result));
    toggleFullscreenOff(instance);
  };
  // engine gameplay sound settings updated callback
  window.gameplaySoundSettingsUpdated = (result: number) => dispatch(Actions.unityGameplaySoundSettingsUpdated(result));
  // engine gameplay background settings updated callback
  window.gameplayBackgroundSettingsUpdated = (result: number) =>
    dispatch(Actions.unityGameplayBackgroundSettingsUpdated(result));
  // engine gameplay fullscreen settings updated callback
  window.gameplayFullscreenSettingsUpdated = (result: number) =>
    dispatch(Actions.unityGameplayFullscreenSettingsUpdated(result));
  // generic callback to save user stats
  window.saveStats = (statId: string, payload: string) => dispatch(Actions.unityUpdateStats(statId, payload));
  // error handlers
  window.exerciseFailed = (e: Error) => dispatch(Actions.unityExerciseFailed(e.message));
  window.onError = (e: Error) => dispatch(Actions.unityExerciseFailed(e.message));
  // alert handler
  window.onAlert = (message) => {
    if (!instance) {
      return;
    }
    dispatch(Actions.unityAlert(message));
    instance.SendMessage('Main', 'OnHandleDialogue', JSON.stringify({ result: true }));
  };
  // tracking event
  window.trackEvent = (eventData: string) => dispatch(Actions.unityTrackEvent(eventData));
  loaderScript = document.createElement('script');
  const BUILD_PATH = `${basePath}/Build`;
  const loaderPath = BUILD_PATH + '/Production.loader.js';
  loaderScript.src = loaderPath;
  loaderScript.onload = () => dispatch(Actions.unityLoaderStart());
  loaderScript.async = true;
  document.body.appendChild(loaderScript);
  dispatch(Actions.unityLog(`${timerName} started at ${startTime}`));
}

export function* unityLoaderStartSaga(dispatch: Dispatch, action: ActionTypes.IUnityInitAction) {
  const { basePath, canvasId, fullscreenClass, codeUrl, dataUrl, frameworkUrl } = action.payload;
  yield put(Actions.unityLog('UnityLoader onLoad called'));
  // unity loader script loaded - ready to load engine
  const onProgress: ProgressCallback = (progress: number) => dispatch(Actions.unityProgressUpdate(progress));
  const BUILD_PATH = `${basePath}/Build`;
  const config = getConfig(BUILD_PATH, dataUrl, codeUrl, frameworkUrl);
  try {
    // try to find given canvas
    const canvas = document.getElementById(canvasId) as HTMLCanvasElement;
    if (!canvas) {
      yield put(Actions.unityError('Can not find canvas to instantiate Unity!'));
      yield put(Actions.unityStop());
      return;
    }
    yield put(Actions.unityLog(`calling createUnityInstance with canvasId=${canvasId}`));
    instance = yield call(window.createUnityInstance, canvas, config, onProgress);
    yield call(registerFullscreenHandler, instance, fullscreenClass);
    yield call(registerResumeContextHandler, instance);
    yield put(Actions.unityLog(`createUnityInstance called with following config: ${JSON.stringify(config)}`));
    yield put(Actions.unityLoadingStart());
  } catch (error) {
    const message = typeof error === 'string' ? error : (error as Error).message;
    yield put(Actions.unityError(`Failed to instantiate Unity! Error: ${message}`));
    yield put(Actions.unityStop());
  }
}

export function* unityInitTransitionSaga(dispatch: Dispatch, action: ActionTypes.IUnityInitAction) {
  // inject UnityLoader
  yield fork(unityInitSaga, dispatch, action);
  // wait for onLoad callback
  yield take(ActionTypes.UNITY_LOADER_START);
  // instantiate app
  yield fork(unityLoaderStartSaga, dispatch, action);
}

// 'app ready' handler called when core app loaded
export function* appReadySaga() {
  yield put(Actions.unityLog('appReady called'));
  yield put(Actions.unityLoadingEnd());
}

export function* appInitSaga(config: UnityEngineAppConfig) {
  if (!endTime) {
    config.startedAt = startTime;
    endTime = getTimestamp();
    yield put(Actions.unityLog(`${timerName} finished at ${endTime} (=${endTime - startTime}s)`));
    console.timeEnd(timerName);
  }
  yield call(sendMessage, 'InitializeApp', JSON.stringify(config));
  yield put(Actions.unityLoadingStart());
}

export function* unityEngineStartupSaga(
  action: ActionTypes.IUnityAppInitAction | ActionTypes.IUnityStopAction | ActionTypes.IUnityExerciseFailedAction
) {
  if (action.type !== ActionTypes.UNITY_APP_INIT) {
    return;
  }
  const config = action.payload;
  yield fork(appInitSaga, config);
  // wait for engineReady
  yield take(ActionTypes.UNITY_ENGINE_READY);
  yield put(Actions.unityLog('engineReady called'));
  yield put(Actions.unityLoadingEnd());
}

export function* exerciseStartupSaga(
  action: ActionTypes.IUnityExerciseInitAction | ActionTypes.IUnityStopAction | ActionTypes.IUnityExerciseFailedAction
) {
  if (action.type !== ActionTypes.UNITY_EXERCISE_INIT) {
    return;
  }
  const settings = action.payload;
  yield call(sendMessage, 'InitializeExercise', JSON.stringify(settings));
  yield put(Actions.unityLoadingStart());
  yield take(ActionTypes.UNITY_EXERCISE_READY);
  yield put(Actions.unityLog('exerciseReady called'));
  yield put(Actions.unityLoadingEnd());
}

export function* exerciseStartSaga(
  action: ActionTypes.IUnityExerciseStartAction | ActionTypes.IUnityStopAction | ActionTypes.IUnityExerciseFailedAction
) {
  if (action.type !== ActionTypes.UNITY_EXERCISE_START) {
    return;
  }
  yield put(Actions.unityExerciseRunning());
  const options = action.payload;
  yield call(sendMessage, 'StartExercise', JSON.stringify(options));
}

// we probably need to refactor this to call SendMessage via call effect
// to make it possible to test the saga properly
export function* sendMessage(methodName: string, value?: any) {
  if (instance) {
    const params = typeof value === 'string' || typeof value === 'undefined' ? value : JSON.stringify(value);
    const logMsg = `calling SendMessage('Main', '${methodName}', '${params}')`;
    yield put(Actions.unityLog(logMsg));
    instance.SendMessage('Main', methodName, params);
  } else {
    yield put(Actions.unityError('Trying to send message to not initialized instance!'));
    yield put(Actions.unityStop());
  }
}

export function* unityFlowStartSaga(dispatch: Dispatch, action: ActionTypes.IUnityInitAction) {
  yield fork(unityInitTransitionSaga, dispatch, action);
  // wait for appReady callback
  yield take(ActionTypes.UNITY_APP_READY);
  yield fork(appReadySaga);
}

export function* exerciseRemoveSaga() {
  yield call(sendMessage, 'RemoveExercise');
}

export default function* unityWatcher(dispatch: Dispatch) {
  yield all([
    takeLatest(ActionTypes.UNITY_INIT, unityFlowStartSaga, dispatch),
    takeLatest(
      [ActionTypes.UNITY_APP_INIT, ActionTypes.UNITY_STOP, ActionTypes.UNITY_EXERCISE_FAILED],
      unityEngineStartupSaga
    ),
    takeLatest(
      [ActionTypes.UNITY_EXERCISE_INIT, ActionTypes.UNITY_STOP, ActionTypes.UNITY_EXERCISE_FAILED],
      exerciseStartupSaga
    ),
    takeLatest(
      [ActionTypes.UNITY_EXERCISE_START, ActionTypes.UNITY_STOP, ActionTypes.UNITY_EXERCISE_FAILED],
      exerciseStartSaga
    ),
    takeLatest(ActionTypes.UNITY_EXERCISE_REMOVE, exerciseRemoveSaga),
  ]);
}
